import { Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface TestimonialsProps {
  section: any;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            'h-4 w-4',
            i <= rating ? 'fill-amber-400 text-amber-400' : 'text-sage-200'
          )}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export default function Testimonials({ section }: TestimonialsProps) {
  const testimonials: any[] = section.testimonials ?? [];
  const headingId = `testimonials-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  if (testimonials.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
          >
            {isActive ? (
              <QuickEditInline
                value={section.title || 'What Our Customers Say'}
                onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                label="Testimonials title"
                as="span"
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              />
            ) : (section.title || 'What Our Customers Say')}
          </h2>
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {isActive ? (
                <QuickEditInline
                  value={section.subtitle}
                  onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                  label="Testimonials subtitle"
                  as="span"
                  className="text-sage-600"
                />
              ) : section.subtitle}
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
            <div className="h-px w-12 bg-sage-300" />
            <Sparkles className="h-4 w-4 text-sage-400" />
            <div className="h-px w-12 bg-sage-300" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((review: any) => (
            <blockquote
              key={review.id}
              className="rounded-xl bg-surface p-6 shadow-sm border border-sage-100"
            >
              <StarRating rating={review.rating} />
              {review.title && (
                <p className="mt-3 font-display font-semibold text-sage-900">
                  {review.title}
                </p>
              )}
              <p className="mt-2 font-display italic text-sage-700 text-sm leading-relaxed">
                &ldquo;{review.body}&rdquo;
              </p>
              <footer className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-sage-800">
                    {review.first_name} {review.last_initial}
                  </p>
                  {review.is_verified_purchase && (
                    <p className="text-xs text-sage-500">Verified Purchase</p>
                  )}
                </div>
                {review.product_slug && (
                  <Link
                    to={`/product/${review.product_slug}`}
                    className="text-xs text-sage-500 hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
                  >
                    {review.product_name}
                  </Link>
                )}
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
